import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, delay, map, mergeMap, of, retry, tap } from 'rxjs';

import { Connection, ConnectionAction, ConnectionCreationProcess, PersonalTaxSync, Platform } from '@/connections/models';
import { PollingStatus } from '@/tax/models';

import { environment } from '@env';

const LONG_DELAY_STATUS_FETCH_MS = 5000;
const DELAY_STATUS_FETCH_MS = 1000;
const invalidErrorMessage = 'Not ready';
const EXPERIMENTAL_PLATFORMS: string[] = [];

@Injectable({
    providedIn: 'root',
})
export class ConnectionService {
    constructor(private http: HttpClient) {}

    getConnections(orgId: number): Observable<Connection[]> {
        return this.http
            .get<any[]>(environment.endpoints.organisationConnectionList(orgId))
            .pipe(map<any[], Connection[]>((connections) => connections.map((connection: any) => new Connection().deserialize(connection))))
            .pipe(map((connections) => connections.sort(Connection.Sort)));
    }

    getConnection(orgId: number, connID: number): Observable<Connection> {
        return this.http
            .get<any>(environment.endpoints.organisationConnectionDetails(orgId, connID))
            .pipe(map<any, Connection>((conn) => new Connection().deserialize(conn)));
    }

    updateConnection(orgId: number, connection: Connection): Observable<Connection> {
        return this.http
            .patch<Connection>(environment.endpoints.organisationConnectionDetails(orgId, connection.id), connection.serialize())
            .pipe(map((conn: any) => new Connection().deserialize(conn)));
    }

    addOrAuthorizeConnection(orgId: number, connection: Connection): Observable<ConnectionAction> {
        return this.http
            .post<ConnectionAction>(environment.endpoints.organisationConnectionList(orgId), connection.serialize())
            .pipe(map((conn: any) => new ConnectionAction().deserialize(conn)));
    }

    getPlatforms(): Observable<Platform[]> {
        return this.http
            .get<Platform[]>(environment.endpoints.connectionPlatformList)
            .pipe(map((platforms) => platforms.map((platform) => new Platform().deserialize(platform))))
            .pipe(
                map((platforms) =>
                    platforms
                        .sort(Platform.Sort)
                        .filter((platform) => environment.showExperimentalFeatures || !EXPERIMENTAL_PLATFORMS.includes(platform.type)),
                ),
            );
    }

    deleteConnection(orgId: number, connectionId: number): Observable<any> {
        return this.http.delete(environment.endpoints.organisationConnectionDetails(orgId, connectionId));
    }

    createMinimalPersonalTaxSync(orgId: number, connectionId: number): Observable<PersonalTaxSync> {
        return this.http
            .post<any>(environment.endpoints.organisationConnectionPersonalTaxSync(orgId, connectionId), {})
            .pipe(map<any, PersonalTaxSync>((sync) => new PersonalTaxSync().deserialize(sync)));
    }

    getMinimalPersonalTaxSync(orgId: any, connectionId: any, personalTaxSyncId: any): Observable<PersonalTaxSync> {
        return this.http
            .get<any>(environment.endpoints.organisationConnectionPersonalTaxSyncDetails(orgId, connectionId, personalTaxSyncId))
            .pipe(map<any, PersonalTaxSync>((sync) => new PersonalTaxSync().deserialize(sync)));
    }

    createMinimalPersonalTaxSyncAndPollResult(orgId: number, connectionId: number): Observable<PersonalTaxSync> {
        return this.createMinimalPersonalTaxSync(orgId, connectionId).pipe(
            delay(LONG_DELAY_STATUS_FETCH_MS),
            mergeMap((newSync) =>
                this.getMinimalPersonalTaxSync(orgId, connectionId, newSync.id).pipe(
                    tap((sync) => {
                        if (sync.status === PollingStatus.Init || sync.status === PollingStatus.InProgress) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(LONG_DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }

    createConnectionCreationProcess(orgId: number, proc: ConnectionCreationProcess): Observable<ConnectionCreationProcess> {
        return this.http
            .post<any>(environment.endpoints.organisationConnectionCreationProcessList(orgId), proc.serialize())
            .pipe(map<any, ConnectionCreationProcess>((process) => new ConnectionCreationProcess().deserialize(process)));
    }

    getConnectionCreationProcess(orgId: number, procId: number): Observable<ConnectionCreationProcess> {
        return this.http
            .get<any>(environment.endpoints.organisationConnectionCreationProcessDetail(orgId, procId))
            .pipe(map<any, ConnectionCreationProcess>((process) => new ConnectionCreationProcess().deserialize(process)));
    }

    updateConnectionCreationProcess(orgId: number, proc: ConnectionCreationProcess): Observable<ConnectionCreationProcess> {
        return this.http
            .patch<any>(environment.endpoints.organisationConnectionCreationProcessDetail(orgId, proc.id), proc.serialize())
            .pipe(map<any, ConnectionCreationProcess>((process) => new ConnectionCreationProcess().deserialize(process)));
    }

    handleConnectionCreationProcessAndPollresult(orgId: number, conn: ConnectionCreationProcess): Observable<ConnectionCreationProcess> {
        const observable =
            conn.id > 0 ? this.updateConnectionCreationProcess(orgId, conn) : this.createConnectionCreationProcess(orgId, conn);
        return observable.pipe(
            delay(DELAY_STATUS_FETCH_MS),
            mergeMap((newConn) =>
                this.getConnectionCreationProcess(orgId, newConn.id).pipe(
                    tap((proc) => {
                        if (
                            proc.status === PollingStatus.Init ||
                            proc.status === PollingStatus.InProgress ||
                            // eslint-disable-next-line no-extra-parens
                            (proc.status === PollingStatus.AwaitInput && proc.response !== undefined)
                        ) {
                            throw Error(invalidErrorMessage);
                        }
                    }),
                    retry({
                        delay: (error) => {
                            if (error.message !== invalidErrorMessage) {
                                throw error;
                            } else {
                                return of(true).pipe(delay(DELAY_STATUS_FETCH_MS));
                            }
                        },
                    }),
                ),
            ),
        );
    }
}
